// @flow
// @flow-runtime warn
import React, { type Node, useMemo, useState, useCallback, useEffect } from 'react';
import clone from 'ramda/es/clone';
import path from 'ramda/es/path';
import inc from 'ramda/es/inc';
import concat from 'ramda/es/concat';
import __ from 'ramda/es/__';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import * as shippingStatuses from 'app/common/actions/shop/order/shippingStatuses';
import * as paymentStatuses from 'app/common/actions/shop/order/paymentStatuses';
import shopApi from 'app/actions/shopApi';
import action from 'app/common/lib/action';

import { type OrderFromList } from './OrdersTypes';
import OrdersContext from './OrdersContext';


export const defaultOrdersLimit = 10;

export type OrdersDataProps = {
  children?: Node,
  userUuid: string,
  simple?: boolean,
  shippingStatus?: $Values<typeof shippingStatuses>,
  paymentStatus?: $Values<typeof paymentStatuses>,
  search?: string,
  limit?: number,
  offset?: number,
  deliveryPointUuid?: string,
}

const OrdersData = ({
  children,
  userUuid,
  shippingStatus,
  paymentStatus,
  search,
  limit = defaultOrdersLimit,
  offset = 0,
  deliveryPointUuid,
}: OrdersDataProps) => {
  const { t } = useTranslation('shop');
  const dispatch = useDispatch();

  const [orders, setOrders] = useState<Array<OrderFromList>>([]);
  const [count, setCount] = useState(0);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [currentPage, setCarrentPage] = useState(0);

  /**
   * Запрос данных
   */

  const requestOrders = useCallback(async (append = false, offset_ = 0, limit_ = defaultOrdersLimit) => {
    const requestPath = 'orders';
    const rest = {
      params: {
        offset: offset_ || offset,
        limit: limit_ || limit,
        ...(deliveryPointUuid
          ? { delivery_point_uuid: deliveryPointUuid }
          : { user_uuid: userUuid }),
        simple: true,
        // number,
        shipping_status: shippingStatus,
        payment_status: paymentStatus,
        search,
      },
    };

    try {
      setLoadingOrders(true);
      const res = await shopApi(requestPath, 'get', rest);
      const orderRes = path(['data', 'data'], res) || [];
      const countRes = path(['data', 'count'], res) || 0;
      setOrders(append ? concat(__, orderRes) : orderRes);
      setCount(countRes);
      setLoadingOrders(false);
      return true;
    } catch (error) {
      const errorSave = {
        err: t('error_api_orders_get'),
        path: requestPath,
        error: clone(error),
      };
      dispatch(action('REQUEST_FAILURE', errorSave));
      return false;
    }
  }, [
    t,
    dispatch,
    limit,
    offset,
    userUuid,
    shippingStatus,
    paymentStatus,
    search,
    deliveryPointUuid,
  ]);

  // автозагрузка при монтировании и изменении входящих данных
  useEffect(() => {
    requestOrders();
  }, [requestOrders]);

  /**
   * Запрос следующей порции данных
   */

  const requestOrdersNext = useCallback(async () => {
    setCarrentPage(inc);
    const shift = inc(currentPage) * limit;
    const res = await requestOrders(true, shift);
    return res;
  }, [currentPage, limit, requestOrders]);

  const contextValue = useMemo(() => ({
    content: {
      orders,
      count,
    },
    loading: {
      orders: loadingOrders,
    },
    request: {
      orders: requestOrders,
      ordersNext: requestOrdersNext,
    },
  }), [
    orders,
    count,
    loadingOrders,
    requestOrders,
    requestOrdersNext,
  ]);

  return (
    <OrdersContext.Provider value={contextValue}>
      {children}
    </OrdersContext.Provider>
  );
};

export default OrdersData;
